import React, { useState } from 'react';
import './DatePicker.css';

function DatePicker({ onDateChange, checkIn, checkOut }) {
  const [showPicker, setShowPicker] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedCheckIn, setSelectedCheckIn] = useState(checkIn ? new Date(checkIn) : null);
  const [selectedCheckOut, setSelectedCheckOut] = useState(checkOut ? new Date(checkOut) : null);
  const [pickerMode, setPickerMode] = useState('checkIn'); // 'checkIn' or 'checkOut'

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleDateClick = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    
    if (pickerMode === 'checkIn') {
      setSelectedCheckIn(date);
      setPickerMode('checkOut');
      if (selectedCheckOut && date >= selectedCheckOut) {
        setSelectedCheckOut(null);
      }
    } else {
      if (date >= selectedCheckIn) {
        setSelectedCheckOut(date);
        setPickerMode('checkIn');
        onDateChange({
          checkIn: selectedCheckIn.toISOString().split('T')[0],
          checkOut: date.toISOString().split('T')[0]
        });
        setShowPicker(false);
      }
    }
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const isDateInRange = (day) => {
    if (!selectedCheckIn || !selectedCheckOut) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date > selectedCheckIn && date < selectedCheckOut;
  };

  const isCheckInDate = (day) => {
    if (!selectedCheckIn) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date.getTime() === selectedCheckIn.getTime();
  };

  const isCheckOutDate = (day) => {
    if (!selectedCheckOut) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date.getTime() === selectedCheckOut.getTime();
  };

  const renderMonth = (monthDate) => {
    const days = [];
    const totalDays = getDaysInMonth(monthDate);
    const firstDay = getFirstDayOfMonth(monthDate);

    // Weekday headers
    const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    const weekDayRow = (
      <div key="weekdays" className="weekday-row">
        {weekDays.map(day => (
          <div key={day} className="weekday">{day}</div>
        ))}
      </div>
    );

    // Empty cells
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days
    for (let day = 1; day <= totalDays; day++) {
      const isCheckIn = isCheckInDate(day);
      const isCheckOut = isCheckOutDate(day);
      const inRange = isDateInRange(day);
      const isPast = new Date(monthDate.getFullYear(), monthDate.getMonth(), day) < new Date();

      days.push(
        <button
          key={day}
          className={`calendar-day ${isCheckIn ? 'check-in' : ''} ${isCheckOut ? 'check-out' : ''} ${inRange ? 'in-range' : ''} ${isPast ? 'past' : ''}`}
          onClick={() => !isPast && handleDateClick(day)}
          disabled={isPast}
        >
          {day}
        </button>
      );
    }

    return (
      <div key={monthDate.toString()} className="month-calendar">
        <h3>{monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
        {weekDayRow}
        <div className="days-grid">
          {days}
        </div>
      </div>
    );
  };

  const currentMonthDate = new Date(currentMonth);
  const nextMonthDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1);

  return (
    <div className="date-picker-wrapper">
      <div className="date-picker-input-group">
        <div className="date-input-item" onClick={() => setShowPicker(true)}>
          <label>When</label>
          <input 
            type="text" 
            placeholder="Add dates"
            value={selectedCheckIn && selectedCheckOut 
              ? `${formatDate(selectedCheckIn)} - ${formatDate(selectedCheckOut)}`
              : selectedCheckIn ? formatDate(selectedCheckIn) : ''
            }
            readOnly
          />
        </div>
        {selectedCheckIn && selectedCheckOut && (
          <button 
            className="clear-dates-btn"
            onClick={() => {
              setSelectedCheckIn(null);
              setSelectedCheckOut(null);
              setPickerMode('checkIn');
              onDateChange({ checkIn: '', checkOut: '' });
            }}
          >
            ✕
          </button>
        )}
      </div>

      {showPicker && (
        <div className="date-picker-modal" onClick={() => setShowPicker(false)}>
          <div className="date-picker-content" onClick={(e) => e.stopPropagation()}>
            <div className="date-picker-header">
              <button className="nav-btn prev" onClick={handlePrevMonth}>←</button>
              <div className="month-nav">
                {renderMonth(currentMonthDate)}
                {renderMonth(nextMonthDate)}
              </div>
              <button className="nav-btn next" onClick={handleNextMonth}>→</button>
            </div>

            <div className="date-picker-footer">
              <button 
                className="close-picker-btn"
                onClick={() => setShowPicker(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DatePicker;
